import Modal from "components/Modal";
import { useEffect, useState } from "react";
import TextInput from "components/Base/TextInput";
import ChartTitlesModal from "components/admin/ChartTitlesModal";
import { getTemplates, createNewRole } from "api/admin/index";
import { notifySuccess, notifyError } from "utils/customToasts";
import loadingGif from "images/loader2.webp";

const CreateRoleModal = ({ open, onClose, initialRoleName = "", onSuccess }) => {
    const [step, setStep] = useState(0);
    const [roleName, setRoleName] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [roleNameError, setRoleNameError] = useState("");
    const [chartTitlesTemplate, setChartTitlesTemplate] = useState(null);

    useEffect(() => {
        if (open) {
            setStep(0);
            setRoleName(initialRoleName);
            setSelectedTemplateId(null);
            setRoleNameError("");
        }
    }, [open, initialRoleName]);

    useEffect(() => {
        if (open && step === 1 && templates.length === 0) {
            setTemplatesLoading(true);
            getTemplates()
                .then((response) => {
                    if (response?.statusCode === "10000" && Array.isArray(response?.data)) {
                        setTemplates(response.data);
                    } else {
                        notifyError(response?.message || "Failed to load templates");
                    }
                })
                .catch(() => {
                    notifyError("Failed to load templates");
                })
                .finally(() => setTemplatesLoading(false));
        }
    }, [open, step, templates.length]);

    const canProceedFromStep0 = roleName.trim().length > 0;

    const handleNext = () => {
        const trimmed = roleName.trim();
        if (!trimmed) {
            setRoleNameError("Role name is required");
            return;
        }
        setRoleNameError("");
        setStep(1);
    };

    const handleCreateRole = async () => {
        if (!selectedTemplateId) {
            notifyError("Please select a template");
            return;
        }
        setSubmitLoading(true);
        try {
            const response = await createNewRole({
                roleName: roleName.trim(),
                mappedDesignations: [],
                templates: [selectedTemplateId],
                permissions: [],
            });
            if (response?.statusCode === "10000" && response?.data) {
                notifySuccess(response?.message || "Role created successfully");
                onSuccess?.({
                    label: response.data.roleName,
                    value: response.data._id,
                });
                onClose();
            } else {
                notifyError(response?.message || "Failed to create role");
            }
        } catch (err) {
            notifyError("Something went wrong while creating the role");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleBack = () => setStep(0);

    const handleClose = () => {
        onClose();
        setStep(0);
        setRoleName("");
        setSelectedTemplateId(null);
    };

    return (
        <div className="relative z-[100]">
            <Modal
                position="top-right"
                className="relative w-[40rem] px-8 py-10 bg-primary-3 text-white rounded-lg text-left"
                open={open}
            >
            <h1 className="text-center text-2xl">
                {step === 0 ? "Create role" : "Select template"}
            </h1>

            {step === 0 && (
                <div className="mt-6 flex flex-col gap-4">
                    <TextInput
                        isRequired
                        label="Role name"
                        name="roleName"
                        value={roleName}
                        onChange={(e) => {
                            setRoleName(e.target.value);
                            if (roleNameError) setRoleNameError("");
                        }}
                        error={roleNameError}
                        placeholder="e.g. Finance Manager"
                    />
                </div>
            )}

            {step === 1 && (
                <div className="mt-6 flex flex-col gap-3">
                    {templatesLoading ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
                            <img src={loadingGif} alt="Loading" className="h-5 w-5" />
                            <span>Loading templates…</span>
                        </div>
                    ) : templates.length === 0 ? (
                        <p className="text-gray-400 py-4">No templates available.</p>
                    ) : (
                        <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                            {templates.map((template) => {
                                const isSelected = selectedTemplateId === template._id;
                                return (
                                    <div
                                        key={template._id}
                                        className={`flex items-center justify-between gap-3 w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                                            isSelected
                                                ? "border-primary-1 bg-primary-1/20 text-white"
                                                : "border-primary-light bg-primary-3 text-gray-300 hover:border-gray-500 hover:bg-primary-3/80"
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="flex-1 min-w-0 text-left font-medium truncate"
                                            onClick={() => setSelectedTemplateId(template._id)}
                                        >
                                            {template.name ?? "Unnamed template"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setChartTitlesTemplate(template);
                                            }}
                                            className="py-1.5 px-3 text-sm rounded bg-primary-1 text-white hover:bg-primary-1/80 shrink-0"
                                        >
                                            View chart
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-end mt-6 gap-4">
                {step === 1 && (
                    <button
                        type="button"
                        disabled={submitLoading}
                        className="py-2 px-4 border border-gray-200 rounded text-gray-300 disabled:opacity-60"
                        onClick={handleBack}
                    >
                        Back
                    </button>
                )}
                <button
                    type="button"
                    className="py-2 px-4 border border-gray-200 rounded text-gray-300"
                    onClick={handleClose}
                >
                    Cancel
                </button>
                {step === 0 ? (
                    <button
                        type="button"
                        disabled={!canProceedFromStep0}
                        className="py-2 bg-primary-1 px-4 rounded text-white disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleNext}
                    >
                        Next
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled={submitLoading || !selectedTemplateId || templatesLoading}
                        className="py-2 bg-primary-1 px-4 rounded text-white flex gap-2 items-center disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleCreateRole}
                    >
                        {submitLoading && (
                            <img src={loadingGif} alt="Loading" className="h-4 w-4" />
                        )}
                        Create role
                    </button>
                )}
            </div>
            </Modal>

            <ChartTitlesModal
                open={!!chartTitlesTemplate}
                onClose={() => setChartTitlesTemplate(null)}
                template={chartTitlesTemplate}
            />
        </div>
    );
};

export default CreateRoleModal;
